import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

const formSchema = z
	.object({
		username: z.string().min(2, {
			message: "ユーザー名は2文字以上である必要があります。",
		}),
		email: z.string().email({
			message: "有効なメールアドレスを入力してください。",
		}),
		password: z.string().min(8, {
			message: "パスワードは8文字以上である必要があります。",
		}),
		confirmPassword: z.string(),
		agreeTerms: z.boolean().refine((val) => val === true, {
			message: "利用規約とプライバシーポリシーに同意する必要があります。",
		}),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "パスワードが一致しません。",
		path: ["confirmPassword"],
	});

const SignupForm: React.FC = () => {
	const { signUp, loading, error } = useAuth();
	const navigate = useNavigate();
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			username: "",
			email: "",
			password: "",
			confirmPassword: "",
			agreeTerms: false,
		},
	});

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		try {
			const result = await signUp(
				values.email,
				values.password,
				values.username,
			);
			if (result.user && result.profile) {
				console.log("サインアップ成功:", result.profile);
				// TODO: ユーザー状態を更新する（例：グローバルステート管理を使用）
				navigate("/dashboard"); // ダッシュボードへリダイレクト
			} else {
				console.error("ユーザープロフィールの作成に失敗しました");
			}
		} catch (error) {
			console.error("サインアップエラー:", error);
			// エラーメッセージを表示するなどの追加の処理を行うことができます
		}
	};
	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="username"
					render={({ field }) => (
						<FormItem>
							<FormLabel>ユーザー名</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>メールアドレス</FormLabel>
							<FormControl>
								<Input type="email" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="password"
					render={({ field }) => (
						<FormItem>
							<FormLabel>パスワード</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="confirmPassword"
					render={({ field }) => (
						<FormItem>
							<FormLabel>パスワード（確認）</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="agreeTerms"
					render={({ field }) => (
						<FormItem className="flex flex-row items-start space-x-3 space-y-0">
							<FormControl>
								<Checkbox
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
							<div className="space-y-1 leading-none">
								<FormLabel>
									<span>
										<Link to="/terms" className="text-blue-600 hover:underline">
											利用規約
										</Link>
										と
										<Link
											to="/privacy-policy"
											className="text-blue-600 hover:underline"
										>
											プライバシーポリシー
										</Link>
										に同意します
									</span>
								</FormLabel>
							</div>
						</FormItem>
					)}
				/>
				{error && <p className="text-red-500 mt-2">{error}</p>}
				<Button type="submit" className="w-full" disabled={loading}>
					{loading ? "登録中..." : "登録"}
				</Button>
			</form>
		</Form>
	);
};

export default SignupForm;
